import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Alumni,
  EmailChoices,
  GenderChoices,
  UserType,
} from "common/types/";

export type BasicDetails = Pick<
  Alumni,
  | "id"
  | "firstName"
  | "lastName"
  | "preferredName"
  | "email"
  | "personalEmail"
  | "preferredEmail"
  | "associateStatus"
  | "gender"
  | "region"
  | "bio"
  | "roles"
  | "ethnicity"
  | "profilePicture"
  | "preferredVideoConferencing"
  | "participatingInteractionTypes"
  | "linkedinUrl"
>;

export type UniversityDetails = Pick<
  Alumni,
  | "attendedUniversity"
  | "university"
  | "universityCourse"
  | "degreeType"
  | "graduationYear"
>;

export type WorkDetails = Pick<
  Alumni,
  "hasGraduateJob" | "company" | "jobTitle" | "sectors" | "employmentHistory"
>;

export type AlumniRegistrationType = {
  activeStep: number;
  basicDetails: BasicDetails;
  universityDetails: UniversityDetails;
  workDetails: WorkDetails;
  metadata: any;
  isNextStep: boolean;
};

export const initialState: AlumniRegistrationType = {
  activeStep: 0,
  basicDetails: {
    id: "",
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    personalEmail: "",
    preferredEmail: EmailChoices.WORK_EMAIL,
    gender: GenderChoices.NOT_SPECIFIED,
    associateStatus: "",
    bio: "",
    region: {
      id: "",
      name: "",
    },
    ethnicity: {
      id: "",
      name: "",
    },
    profilePicture: "",
    preferredVideoConferencing: "",
    participatingInteractionTypes: [],
    roles: [UserType.ALUMNI],
    linkedinUrl: "",
  },
  universityDetails: {
    attendedUniversity: true,
    university: {
      id: "",
      name: "",
    },
    universityCourse: {
      id: "",
      name: "",
    },
    degreeType: {
      id: "",
      name: "",
    },
    graduationYear: "",
  },
  workDetails: {
    company: "",
    jobTitle: "",
    sectors: [],
    employmentHistory: [],
    hasGraduateJob: true,
  },
  metadata: {
    isDisabled: false,
    isGhost: false,
    emails: {
      receiveDailyEmail: true,
      receiveRequestEmail: true,
      receiveNewMessageEmail: true,
      receiveReminderEmail: true,
      receiveFeedbackEmail: true,
    }
  },
  isNextStep: false,
};

const slice = createSlice({
  name: "alumniRegistration",
  initialState,
  reducers: {
    goToNextStep: (state) => {
      state.activeStep += 1;
    },
    goToPrevStep: (state) => {
      state.activeStep -= 1;
    },
    setBasicDetails: (state, action: PayloadAction<any>) => {
      state.basicDetails = action.payload;
    },
    setUniversityDetails: (state, action: PayloadAction<any>) => {
      state.universityDetails = action.payload;
    },
    setWorkDetails: (state, action: PayloadAction<any>) => {
      state.workDetails = action.payload;
    },
    setAlumniMetadata: (state, action: PayloadAction<any>) => {
      state.metadata = action.payload;
    },
    setIsNextStep: (state, action: PayloadAction<any>) => {
      state.isNextStep = action.payload;
    },
    setActiveStep: (state, action: PayloadAction<any>) => {
      state.activeStep = action.payload;
    },
  },
});

export const {
  goToNextStep,
  goToPrevStep,
  setBasicDetails,
  setUniversityDetails,
  setWorkDetails,
  setAlumniMetadata,
  setIsNextStep,
  setActiveStep,
} = slice.actions;

export default slice.reducer;
